import { apiUserAccountService } from '@api/services/account.apiService';
import { homeRoutes } from '@components-register/home.routes';
import { NotificationDialog } from '@components-shared/modal';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { navigateToURL } from '@utils/navigateToURL';
import i18next from 'i18next';
import { makeAutoObservable } from 'mobx';
import type { MobxRootStore } from './mobx.store';

const ASP_NET_COOKIE_EXP = '.AspNet.Cookies.ExpirationDate';

export class AuthStore {
  mobxStore: MobxRootStore;

  private _visibilityChangeCallback;

  private fetchCookieIfNotExist = async () => {
    const cookieExp = localStorage.getItem(ASP_NET_COOKIE_EXP);

    if (!cookieExp) {
      await this.fetchCookieExpirationDate();
    }
  };

  private getIsAuthRecordValidOrNotExist = () => {
    const cookieExp = localStorage.getItem(ASP_NET_COOKIE_EXP);

    if (!cookieExp || new Date(cookieExp) > new Date()) {
      return true;
    }

    return false;
  };

  constructor(mobxStore: MobxRootStore) {
    this.mobxStore = mobxStore;
    makeAutoObservable(this);

    this._visibilityChangeCallback = async () => {
      if (!document.hidden) {
        await this.fetchCookieIfNotExist();
        void this.validateCookieExpiration();
      }
    };
  }

  startAuthCookieExpirationDateCheckListener = () => {
    document.addEventListener('visibilitychange', this._visibilityChangeCallback);
  };

  stopAuthCookieExpirationDateCheckListener = () => {
    document.removeEventListener('visibilitychange', this._visibilityChangeCallback);
  };

  signIn = async (params: {
    email: string;
    password: string;
    otp?: string;
    captchaValue: string | null | undefined;
    referralToken?: string | null;
  }) => {
    const result = await apiUserAccountService.signIn(params);

    await this.fetchCookieExpirationDate();

    return result;
  };

  signOut = async ({
    returnUrl,
    navigateUrl,
    skipSignOutRequest,
  }: {
    returnUrl?: string;
    navigateUrl?: string;
    skipSignOutRequest?: boolean;
  } = {}) => {
    const returnUrlQuery = !returnUrl ? '' : `?returnUrl=${returnUrl}`;

    try {
      if (!skipSignOutRequest) {
        await apiUserAccountService.signOut();
      }

      localStorage.removeItem(ASP_NET_COOKIE_EXP);
      navigateToURL(navigateUrl ?? `${homeRoutes.signIn}${returnUrlQuery}`);
    } catch (error) {
      handleError(i18next.t(keyMap.accountFailedToSignOutError), error);
    }
  };

  fetchCookieExpirationDate = async () => {
    try {
      const authCookieExpirationDate = (await apiUserAccountService.getAuthCookieExpirationDate()).data;

      localStorage.setItem(ASP_NET_COOKIE_EXP, authCookieExpirationDate.expirationDateTimeUtc);

      return authCookieExpirationDate.expirationDateTimeUtc;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  validateCookieExpiration = () => {
    if (this.getIsAuthRecordValidOrNotExist()) {
      return true;
    }

    this.signOutAndShowSessionExpiredNotificationDialog();
    return false;
  };

  signOutAndShowSessionExpiredNotificationDialog = ({
    skipSignOutRequest,
  }: {
    skipSignOutRequest?: boolean;
  } = {}) => {
    NotificationDialog.show(keyMap.sharedSystemError401, 'error');

    setTimeout(() => {
      void this.signOut({
        skipSignOutRequest,
        returnUrl: encodeURIComponent(window.location.href),
      });
    }, 500);
  };
}
